/**
 * useIframeResize Hook
 *
 * Communicates widget state changes to parent window via postMessage
 */

import { useEffect, useRef, useCallback } from "react";

const MESSAGE_TYPE = "chekmenu-concierge-resize";
const DEBOUNCE_MS = 100;

interface ResizeMessage {
  type: typeof MESSAGE_TYPE;
  state: "open" | "closed";
  height: number;
}

/**
 * Hook to observe container size and communicate to parent iframe
 */
export function useIframeResize<T extends HTMLElement>(isOpen: boolean) {
  const containerRef = useRef<T>(null);
  const lastHeightRef = useRef<number>(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  /**
   * Send message to parent window
   */
  const sendMessage = useCallback(
    (height: number) => {
      // Only send if in iframe
      if (window.parent === window) return;

      const message: ResizeMessage = {
        type: MESSAGE_TYPE,
        state: isOpen ? "open" : "closed",
        height,
      };

      window.parent.postMessage(message, "*");
    },
    [isOpen],
  );

  /**
   * Send height update (debounced)
   */
  const sendHeight = useCallback(
    (height: number) => {
      if (height === lastHeightRef.current) return;
      lastHeightRef.current = height;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        sendMessage(height);
      }, DEBOUNCE_MS);
    },
    [sendMessage],
  );

  // Observe container size changes
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const height = Math.ceil(
        entry.borderBoxSize?.[0]?.blockSize || element.offsetHeight,
      );
      sendHeight(height);
    });

    observer.observe(element);

    // Send initial size
    setTimeout(() => {
      sendHeight(element.offsetHeight);
    }, 100);

    return () => {
      observer.disconnect();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [sendHeight]);

  // Send state change when isOpen changes
  useEffect(() => {
    if (containerRef.current) {
      sendMessage(containerRef.current.offsetHeight);
    }
  }, [isOpen, sendMessage]);

  return containerRef;
}
